import { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Calendar, MapPin, Clock, Trash2, X } from 'lucide-react';
import { apiClient } from '@/lib/api';
import type { Workgroup } from './types';

interface Props {
  workgroup: Workgroup;
  canManage: boolean;
}

interface WorkgroupEvent {
  id: string;
  workgroup_id: string;
  title: string;
  description: string | null;
  location: string | null;
  start_at: string;
  end_at: string | null;
  created_by: string;
  creator_first_name?: string | null;
  creator_last_name?: string | null;
  created_at: string;
}

interface EventDraft {
  title: string;
  description: string;
  location: string;
  start_at: string;
  end_at: string;
}

const emptyDraft: EventDraft = { title: '', description: '', location: '', start_at: '', end_at: '' };

export default function EventsTab({ workgroup, canManage }: Props) {
  const [events, setEvents] = useState<WorkgroupEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<EventDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const res = await apiClient.get<WorkgroupEvent[]>(
        `/messenger/workgroups/${workgroup.id}/events?month=${monthStr}`,
      );
      setEvents(res.data ?? []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    if (!draft.title.trim() || !draft.start_at) return;
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        location: draft.location.trim() || null,
        start_at: draft.start_at,
        end_at: draft.end_at || null,
      };
      if (editingId) {
        await apiClient.patch(`/messenger/workgroups/${workgroup.id}/events/${editingId}`, payload);
      } else {
        await apiClient.post(`/messenger/workgroups/${workgroup.id}/events`, payload);
      }
      setShowForm(false);
      setDraft(emptyDraft);
      setEditingId(null);
      load();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Eliminare l\'evento?')) return;
    await apiClient.delete(`/messenger/workgroups/${workgroup.id}/events/${id}`);
    load();
  }

  function startEdit(ev: WorkgroupEvent) {
    setEditingId(ev.id);
    setDraft({
      title: ev.title,
      description: ev.description ?? '',
      location: ev.location ?? '',
      start_at: ev.start_at.slice(0, 16),
      end_at: ev.end_at ? ev.end_at.slice(0, 16) : '',
    });
    setShowForm(true);
  }

  return (
    <div className="p-4 space-y-4">
      {canManage && (
        !showForm ? (
          <button
            onClick={() => { setEditingId(null); setDraft(emptyDraft); setShowForm(true); }}
            className="px-4 py-2 bg-primary hover:bg-primary/90 rounded-lg text-primary-foreground text-sm flex items-center gap-2"
          >
            <Plus size={14} /> Nuovo evento
          </button>
        ) : (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-foreground font-semibold">{editingId ? 'Modifica evento' : 'Nuovo evento'}</h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-muted-foreground hover:text-foreground">
                <X size={16} />
              </button>
            </div>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Titolo *"
              className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-foreground text-sm"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="datetime-local"
                value={draft.start_at}
                onChange={(e) => setDraft({ ...draft, start_at: e.target.value })}
                className="bg-muted border border-border rounded-lg px-3 py-2 text-foreground text-sm"
              />
              <input
                type="datetime-local"
                value={draft.end_at}
                onChange={(e) => setDraft({ ...draft, end_at: e.target.value })}
                className="bg-muted border border-border rounded-lg px-3 py-2 text-foreground text-sm"
              />
            </div>
            <input
              type="text"
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
              placeholder="Luogo / link"
              className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-foreground text-sm"
            />
            <textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Descrizione"
              rows={3}
              className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-foreground text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={handleSave}
                disabled={saving || !draft.title.trim() || !draft.start_at}
                className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-lg text-primary-foreground text-sm flex items-center gap-2"
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                Salva
              </button>
            </div>
          </div>
        )
      )}

      {loading && <div className="text-center py-8 text-muted-foreground"><Loader2 className="animate-spin inline" /></div>}
      {!loading && events.length === 0 && (
        <div className="text-center py-8 text-muted-foreground/80 text-sm">Nessun evento questo mese</div>
      )}

      <div className="space-y-2">
        {events.map(ev => (
          <div key={ev.id} className="bg-card border border-border rounded-xl p-3 flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 text-blue-300 flex flex-col items-center justify-center flex-shrink-0">
              <Calendar size={14} />
              <span className="text-[10px] font-semibold">{new Date(ev.start_at).getDate()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-foreground font-medium truncate">{ev.title}</div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                <span className="flex items-center gap-1">
                  <Clock size={10} /> {new Date(ev.start_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
                {ev.location && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin size={10} /> {ev.location}
                  </span>
                )}
              </div>
              {ev.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ev.description}</p>}
            </div>
            {canManage && (
              <div className="flex items-center gap-1">
                <button onClick={() => startEdit(ev)} className="text-xs text-muted-foreground hover:text-foreground px-2">
                  edit
                </button>
                <button onClick={() => handleDelete(ev.id)} className="text-muted-foreground hover:text-red-400 p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
